import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Termos de uso e política de privacidade (rascunho — REVISAR COM ADVOGADO antes de vender).
 * Preencha os campos entre colchetes em EMPRESA abaixo. Mudou o texto: suba VERSAO_TERMOS.
 */
export const VERSAO_TERMOS = '2026-10-28';
const EMPRESA = {
  nome: '[RAZÃO SOCIAL]',
  cnpj: '[CNPJ]',
  cidade: '[CIDADE/UF]',
  email: 'contato@fogami.com',
};

function Pagina({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <main className="mx-auto max-w-3xl px-4 py-10 text-sm leading-relaxed">
      <Link to="/" className="text-primary hover:underline">
        ← Voltar
      </Link>
      <h1 className="mt-4 text-2xl font-semibold">{titulo}</h1>
      <p className="mb-6 text-muted-foreground">
        Versão de {VERSAO_TERMOS.split('-').reverse().join('/')}
      </p>
      <div className="flex flex-col gap-4 [&_h2]:mt-4 [&_h2]:text-base [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc">
        {children}
      </div>
    </main>
  );
}

export function TermosDeUso() {
  return (
    <Pagina titulo="Termos de uso">
      <p>
        Estes termos regulam o uso do sistema de gestão para revendas de veículos (o “Sistema”),
        oferecido por {EMPRESA.nome}, CNPJ {EMPRESA.cnpj} (a “Empresa”), à revenda que contrata (a
        “Loja”). Ao criar a conta ou usar o Sistema, a Loja concorda com estes termos.
      </p>
      <h2>1. O que o Sistema faz</h2>
      <p>
        O Sistema é uma ferramenta online para a Loja organizar estoque, vendas, clientes,
        atendimento, financeiro, documentos e o site da loja. As funções disponíveis dependem do
        plano e dos módulos contratados, descritos na tela de assinatura.
      </p>
      <h2>2. Responsabilidades da Loja</h2>
      <ul>
        <li>Manter os dados corretos e atualizados e guardar as próprias senhas.</li>
        <li>
          Usar o Sistema conforme a lei, inclusive a LGPD, o Código de Defesa do Consumidor e as
          regras de trânsito e de anúncios.
        </li>
        <li>
          <strong>Impostos e notas fiscais:</strong> as regras fiscais (CFOP, CST, alíquotas) são
          cadastradas pela Loja com o contador dela. O Sistema só emite nota em produção com a
          validação do contador da Loja. A apuração e o pagamento dos tributos são de
          responsabilidade da Loja e do seu contador.
        </li>
        <li>
          Conferir contratos, recibos, valores de financiamento, juros e multas antes de entregar ao
          cliente final. Os modelos do Sistema são ponto de partida e não substituem orientação
          jurídica ou contábil.
        </li>
        <li>Responder pelas informações que publica no site e nos portais de anúncio.</li>
      </ul>
      <h2>3. Responsabilidades da Empresa</h2>
      <ul>
        <li>
          Manter o Sistema funcionando com cuidado profissional, com cópias de segurança diárias.
        </li>
        <li>Proteger os dados com isolamento entre lojas, criptografia e controle de acesso.</li>
        <li>Avisar com antecedência mudanças relevantes de preço ou de funcionamento.</li>
        <li>Atender o suporte pelos canais do Sistema em horário comercial.</li>
      </ul>
      <h2>4. Pagamentos</h2>
      <p>
        A assinatura é cobrada conforme o plano escolhido. Em caso de atraso, após aviso, o acesso
        às telas pode ser suspenso até o pagamento; os dados da Loja, o site e os anúncios continuam
        guardados. Valores recebidos por Pix e cartão das vendas da Loja vão direto para a conta da
        própria Loja no provedor de pagamento; a Empresa não recebe nem guarda esse dinheiro.
      </p>
      <h2>5. Limitação de responsabilidade</h2>
      <p>
        A Empresa não responde por decisões comerciais, fiscais ou jurídicas tomadas pela Loja com
        base no Sistema, nem por falhas de serviços de terceiros (como Sefaz, bancos, portais,
        WhatsApp e provedores de internet). Em qualquer caso, a responsabilidade da Empresa fica
        limitada ao valor pago pela Loja nos 12 meses anteriores ao fato.
      </p>
      <h2>6. Cancelamento e dados</h2>
      <p>
        A Loja pode cancelar quando quiser pela tela de assinatura. Depois do cancelamento, a Loja
        pode pedir a exportação dos seus dados por até 90 dias; após esse prazo os dados podem ser
        apagados, exceto o que a lei obrigar a guardar.
      </p>
      <h2>7. Mudanças e foro</h2>
      <p>
        Estes termos podem ser atualizados; a versão nova vale a partir da publicação, com aviso no
        Sistema. Fica eleito o foro de {EMPRESA.cidade}. Dúvidas: {EMPRESA.email}. Veja também a{' '}
        <Link to="/privacidade" className="text-primary hover:underline">
          Política de privacidade
        </Link>
        .
      </p>
    </Pagina>
  );
}

export function Privacidade() {
  return (
    <Pagina titulo="Política de privacidade">
      <p>
        Esta política explica como o Sistema trata dados pessoais, conforme a Lei Geral de Proteção
        de Dados (Lei 13.709/2018).
      </p>
      <h2>1. Papéis</h2>
      <p>
        Para os dados dos clientes da Loja (compradores, leads, avalistas), a Loja é a controladora
        e a Empresa é a operadora: tratamos esses dados só para prestar o serviço, sob as instruções
        da Loja. Para os dados de cadastro da própria Loja e dos seus usuários, a Empresa é a
        controladora.
      </p>
      <h2>2. Quais dados e por quê</h2>
      <ul>
        <li>
          Usuários da Loja: nome, e-mail, telefone e registros de acesso, para login e segurança.
        </li>
        <li>
          Clientes da Loja: dados de contato, documentos e informações da venda, para as funções que
          a Loja usa (atendimento, contratos, notas, cobrança).
        </li>
        <li>Mensagens de marketing só com consentimento registrado do cliente.</li>
      </ul>
      <h2>3. Compartilhamento</h2>
      <p>
        Só com fornecedores necessários ao serviço (hospedagem, e-mail, pagamentos, nota fiscal,
        WhatsApp), quando a Loja usa essas funções, e com autoridades quando a lei exigir. Não
        vendemos dados.
      </p>
      <h2>4. Segurança</h2>
      <p>
        Isolamento de dados entre lojas, senhas protegidas, código de acesso no celular para quem vê
        dados sensíveis, CPF guardado criptografado, cópias de segurança e registro de quem acessou
        o quê. O suporte só entra na conta da Loja com autorização dela, por tempo limitado e com
        registro.
      </p>
      <h2>5. Direitos</h2>
      <p>
        O titular pode pedir acesso, correção, portabilidade ou exclusão dos dados à Loja (para
        dados de clientes) ou pelo e-mail {EMPRESA.email}. A Loja consegue exportar os dados de um
        cliente pela ficha dele.
      </p>
      <h2>6. Guarda</h2>
      <p>
        Os dados ficam guardados enquanto a Loja usar o Sistema e pelo prazo que a lei exigir (por
        exemplo, documentos fiscais).
      </p>
      <p>
        Encarregado (DPO): {EMPRESA.nome} — {EMPRESA.email}.{' '}
        <Link to="/termos" className="text-primary hover:underline">
          Termos de uso
        </Link>
      </p>
    </Pagina>
  );
}
